import { api } from "./apiClient";

export interface DebugAutoFinishProject {
  projectId: string;
  mmCode: number;
  eventId: string;
  eventName: string;
  currentStatus: string;
  updateStatusOfPastEvent: boolean;
  endDate: string;
  endDateFormatted: string;
  targetD1: string;
  currentDateFromApi: string;
  nextRoutineDate: string;
  shouldFinish: boolean;
  willFinishTomorrow: boolean;
  reason: string;
}

export interface DebugAutoFinishResponse {
  debugInfo: {
    timestamp: string;
    currentTimeFromApi: string;
    nextRoutineDate: string;
    timezone: string;
    gmtOffset: number;
    totalCandidates: number;
    wouldFinish: number;
    wouldNotFinish: number;
    wouldFinishToday: number;
    wouldFinishTomorrow: number;
  };
  projects: DebugAutoFinishProject[];
  executionResult: {
    executed: boolean;
    finishedProjects?: number;
    details?: any[];
    message?: string;
  };
}

export interface DebugAutoFinishByEventIdResponse {
  error?: boolean;
  message?: string;
  debugInfo?: {
    timestamp: string;
    currentTimeFromApi: string;
    timezone: string;
    gmtOffset: number;
  };
  project?: {
    projectId: string;
    mmCode: number;
    eventId: string;
    eventName: string;
  };
  status?: {
    currentStatus: string;
    isApproved: boolean;
    updateStatusOfPastEventFromEvent: boolean;
    updateStatusOfPastEventFromStatus: boolean;
    notYetFinishedAutomatically: boolean;
  };
  dates?: {
    endDate: string;
    endDateFormatted: string;
    targetD1: string;
    currentDateFromApi: string;
  };
  analysis?: {
    isEligibleForAutoFinish: boolean;
    hasPassedD1: boolean;
    wouldBeFinishedByRobot: boolean;
    reasons: string[];
    conclusion: string;
  };
}

export default class RoutinesService {
  /**
   * Obtém informações de debug sobre finalização automática
   * @param execute Se true, executa a finalização
   */
  static async getDebugAutoFinish(
    execute: boolean = false
  ): Promise<DebugAutoFinishResponse> {
    const url = execute
      ? "/project/debug-auto-finish?execute=true"
      : "/project/debug-auto-finish";
    const response = await api.get<DebugAutoFinishResponse>(url);
    return response.data;
  }

  /**
   * Obtém informações de debug para um evento específico
   */
  static async getDebugAutoFinishByEventId(
    eventId: string
  ): Promise<DebugAutoFinishByEventIdResponse> {
    const response = await api.get<DebugAutoFinishByEventIdResponse>(
      `/project/debug-auto-finish/${eventId}`
    );
    return response.data;
  }

  /**
   * Força execução da rotina de finalização
   */
  static async forceExecuteFinishRoutine(): Promise<DebugAutoFinishResponse> {
    return this.getDebugAutoFinish(true);
  }

  /**
   * Prévia ou disparo do lote D-1 de hoje. Só entram projetos cujo evento
   * começa amanhã.
   */
  static async getPreEventD1Debug(
    execute: boolean = false
  ): Promise<PreEventD1DebugResponse> {
    const url = execute
      ? "/project/debug-pre-event-d1?execute=true"
      : "/project/debug-pre-event-d1";
    const response = await api.get<PreEventD1DebugResponse>(url);
    return response.data;
  }
}

export interface PreEventD1DebugProject {
  projectId: string;
  mmCode?: string | number;
  eventName?: string;
  action: string;
  reason?: string;
  budgetApproved?: boolean;
  retryEmailOnly?: boolean;
  emailSent?: boolean;
}

export interface PreEventD1DebugResponse {
  execute: boolean;
  examined: number;
  executed: number;
  emailed: number;
  skipped: number;
  emailPending: number;
  projects: PreEventD1DebugProject[];
}
